import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Apple, ChevronDown, ChevronUp } from "lucide-react";

const MEAL_LABELS = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snacks" };

export const ClientFoodLogCard = ({ clientId }) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [date, setDate] = useState(null);
  const [day, setDay] = useState(null);

  useEffect(() => {
    if (!open || summary) return;
    api
      .get(`/coaching/clients/${clientId}/food-summary`, { params: { days: 7 } })
      .then((r) => {
        setSummary(r.data);
        const firstLogged = r.data.days.find((d) => d.entries > 0);
        setDate((firstLogged || r.data.days[0]).date);
      })
      .catch(() => {});
  }, [open, summary, clientId]);

  useEffect(() => {
    if (!date) return;
    api
      .get(`/coaching/clients/${clientId}/food-log`, { params: { date } })
      .then((r) => setDay(r.data))
      .catch(() => setDay(null));
  }, [date, clientId]);

  const targets = summary?.targets;

  return (
    <div className="obs-card" data-testid="client-foodlog-card">
      <button
        type="button"
        data-testid="client-foodlog-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <Apple className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Food diary</div>
            <h3 className="font-heading text-2xl uppercase">What they're eating</h3>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {summary && (
            <>
              <div className="grid grid-cols-3 gap-3" data-testid="client-foodlog-summary">
                <div className="stat-tile">
                  <div className="overline">Days logged (7d)</div>
                  <div className="font-data text-2xl">{summary.days_logged}/7</div>
                </div>
                <div className="stat-tile">
                  <div className="overline">Avg kcal</div>
                  <div className="font-data text-2xl">
                    {summary.avg_calories ?? "—"}
                    {targets?.calories && <span className="text-xs text-zinc-500"> / {targets.calories}</span>}
                  </div>
                </div>
                <div className="stat-tile">
                  <div className="overline">Avg protein</div>
                  <div className="font-data text-2xl text-blaze">
                    {summary.avg_protein_g ?? "—"}
                    {targets?.protein_g && <span className="text-xs text-zinc-500"> / {targets.protein_g}g</span>}
                  </div>
                </div>
              </div>

              {/* 7-day strip */}
              <div className="flex flex-wrap gap-2" data-testid="client-foodlog-days">
                {summary.days.map((d) => (
                  <button
                    key={d.date}
                    onClick={() => setDate(d.date)}
                    data-testid={`foodlog-day-${d.date}`}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border rounded-sm transition-colors ${
                      date === d.date
                        ? "border-blaze bg-blaze text-white"
                        : d.entries > 0
                        ? "border-obsidian-500 text-zinc-300 hover:border-blaze"
                        : "border-obsidian-600 text-zinc-600 hover:border-obsidian-500"
                    }`}
                  >
                    {new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}
                    <span className="block font-data normal-case tracking-normal">
                      {d.calories != null ? `${Math.round(d.calories)} kcal` : "—"}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {day && (
            <div data-testid="client-foodlog-day">
              {day.entries.length === 0 ? (
                <p className="text-zinc-500 text-sm">Nothing logged on this day.</p>
              ) : (
                <>
                  <div className="text-xs text-zinc-400 font-data mb-2">
                    Day total: {day.totals.calories} kcal · {day.totals.protein_g}g P · {day.totals.carbs_g}g C · {day.totals.fat_g}g F
                    {day.targets?.calories && <span className="text-zinc-500"> (target {day.targets.calories} kcal)</span>}
                  </div>
                  {["breakfast", "lunch", "dinner", "snack"].map((slot) => {
                    const es = day.entries.filter((e) => e.meal === slot);
                    if (es.length === 0) return null;
                    return (
                      <div key={slot} className="mb-3">
                        <div className="overline mb-1">{MEAL_LABELS[slot]}</div>
                        <div className="divide-y divide-obsidian-600/50">
                          {es.map((e) => (
                            <div key={e.id} className="flex items-center justify-between gap-2 py-1.5">
                              <div className="min-w-0">
                                <span className="text-sm truncate">{e.name}</span>
                                {e.qty_desc && <span className="text-[11px] text-zinc-500 ml-2">{e.qty_desc}</span>}
                              </div>
                              <div className="font-data text-xs text-zinc-400 shrink-0">
                                {e.calories} kcal · <span className="text-blaze">{e.protein_g}P</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientFoodLogCard;
